/**
 * Builds the Calculation & Assumption Summary.
 *
 * This document records what the user entered and what the arithmetic produced.
 * It is not a validation report, a regulatory report, an approval, or a
 * certification, and the text below must not drift in that direction.
 */

import type { CalculationResult, SensitivityRow, TemperatureScenarioRow } from '../types';
import { APP_META, DISCLAIMER_FULL, RESEARCH_PROVENANCE } from '../data/provenance';
import { CONVERSION_CONVENTION } from '../calculator/units';
import { REFERENCES_QUALIFICATION } from '../data/references';
import { formatDate, formatExact, formatNumber, formatTimestamp } from './format';

export interface SummaryContext {
  result: CalculationResult;
  sensitivity: SensitivityRow[];
  temperatureScenarios: TemperatureScenarioRow[];
  consideredIds: string[];
  totalConsiderations: number;
  verificationHeadline: string;
  developmentPassed: number;
  developmentTotal: number;
}

function rule(char = '-'): string {
  return char.repeat(68);
}

export function buildSummaryText(ctx: SummaryContext): string {
  const { result, sensitivity, temperatureScenarios } = ctx;
  const i = result.inputs;
  const lines: string[] = [];

  lines.push('CALCULATION & ASSUMPTION SUMMARY');
  lines.push(rule('='));
  lines.push(`${APP_META.name} — ${APP_META.version}`);
  lines.push(`Generated: ${formatTimestamp(result.computedAt)}`);
  lines.push(`Calculator verification version: ${APP_META.calculatorVerificationVersion}`);
  lines.push(`Verification dataset: ${APP_META.verificationDatasetVersion}`);
  lines.push('');
  lines.push('This document records the values entered by the user and the result');
  lines.push('of the arithmetic. It does not state that those values are appropriate.');
  lines.push('');

  lines.push('INPUTS AS ENTERED');
  lines.push(rule());
  lines.push(`Desired real-time shelf life : ${i.realTimeDurationValue} ${i.realTimeDurationUnit}`);
  lines.push(`Reference temperature (T_RT) : ${i.referenceTempC} °C`);
  lines.push(`Accelerated temperature (T_AA): ${i.acceleratedTempC} °C`);
  lines.push(`Q10 (assumption)             : ${i.q10}`);
  lines.push(`Study start date             : ${i.startDate ? formatDate(i.startDate) : 'not entered'}`);
  lines.push('');
  lines.push(`Duration conversion convention: ${CONVERSION_CONVENTION.summary}`);
  lines.push('');

  lines.push('EQUATIONS');
  lines.push(rule());
  lines.push('AAF = Q10 ^ ((T_AA - T_RT) / 10)');
  lines.push('AAT = RTD / AAF');
  lines.push('');

  lines.push('DERIVATION');
  lines.push(rule());
  result.derivation.forEach((step, index) => {
    lines.push(`${index + 1}. ${step.label}`);
    if (step.substituted) step.substituted.split('\n').forEach((s) => lines.push(`   ${s}`));
    if (step.result) lines.push(`   -> ${step.result}`);
    if (step.note) lines.push(`   note: ${step.note}`);
  });
  lines.push('');

  lines.push('RESULTS');
  lines.push(rule());
  lines.push(`Accelerated aging factor (AAF), exact : ${formatExact(result.aaf)}`);
  lines.push(`Accelerated aging duration (AAT), exact: ${formatExact(result.aatDays)} days`);
  lines.push(`AAT rounded up to whole days           : ${result.aatDaysWholeCeiling} days`);
  if (result.projectedCompletionDate) {
    lines.push(`Projected completion date              : ${formatDate(result.projectedCompletionDate)}`);
  }
  lines.push('');
  lines.push('The rounded figure is a display convention. The scheduled study');
  lines.push('duration is an engineering test-plan decision, not an output here.');
  lines.push('');

  lines.push('Q10 SENSITIVITY');
  lines.push(rule());
  lines.push('Q10        AAF            AAT (days)     vs. entered Q10');
  sensitivity.forEach((row) => {
    const marker = row.isBaseline ? ' <- entered' : '';
    lines.push(
      `${String(row.q10).padEnd(10)} ${formatNumber(row.aaf, 4).padEnd(14)} ${formatNumber(row.aatDays, 3).padEnd(14)} ${formatNumber(
        row.deltaDaysVsBaseline,
        2,
      ).padEnd(10)}${marker}`,
    );
  });
  lines.push('');
  lines.push('No Q10 value shown above is endorsed by this tool.');
  lines.push('');

  if (temperatureScenarios.length > 0) {
    lines.push('TEMPERATURE SCENARIOS EXPLORED');
    lines.push(rule());
    lines.push('T_AA (°C)  AAF            AAT (days)     vs. entered T_AA');
    temperatureScenarios.forEach((row) => {
      const marker = row.isBaseline ? ' <- entered' : '';
      lines.push(
        `${String(row.acceleratedTempC).padEnd(10)} ${formatNumber(row.aaf, 4).padEnd(14)} ${formatNumber(
          row.aatDays,
          3,
        ).padEnd(14)} ${formatNumber(row.deltaDaysVsBaseline, 2).padEnd(10)}${marker}`,
      );
    });
    lines.push('');
    lines.push('No temperature shown above is recommended by this tool.');
    lines.push('');
  }

  lines.push('ENGINEERING CONSIDERATIONS REVIEWED');
  lines.push(rule());
  lines.push(`${ctx.consideredIds.length} of ${ctx.totalConsiderations} checklist items marked as reviewed by the user.`);
  lines.push('This count reflects self-reported review only. It is not an approval,');
  lines.push('an assessment of completeness, or a compliance determination.');
  lines.push('');

  lines.push('CALCULATOR VERIFICATION STATUS');
  lines.push(rule());
  lines.push(`Headline status: ${ctx.verificationHeadline}`);
  lines.push(`Development test cases passed: ${ctx.developmentPassed} of ${ctx.developmentTotal}`);
  lines.push('Development tests were authored alongside the implementation. They');
  lines.push('demonstrate internal consistency, not independent verification.');
  lines.push('');

  lines.push('RESEARCH PROVENANCE');
  lines.push(rule());
  RESEARCH_PROVENANCE.forEach((entry) => lines.push(`${entry.label}: ${entry.value}`));
  lines.push('');

  lines.push('REFERENCE QUALIFICATION');
  lines.push(rule());
  lines.push(REFERENCES_QUALIFICATION);
  lines.push('');

  lines.push('SCOPE AND LIMITATIONS');
  lines.push(rule('='));
  DISCLAIMER_FULL.forEach((paragraph) => {
    lines.push(paragraph);
    lines.push('');
  });

  return lines.join('\n');
}
